import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;

const Page = styled.div`
  min-height: 100vh; background: #0a0a0f; color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif; padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #1e2030;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer;
  padding: 8px; border-radius: 8px;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #00c6ff, #0072ff);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;

const HistoryList = styled.div`display: flex; flex-direction: column; gap: 12px;`;
const HistoryCard = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 12px;
  overflow: hidden; animation: ${fadeIn} 0.3s ease;
`;
const HistoryHeader = styled.div`
  display: flex; align-items: center; gap: 16px;
  padding: 14px 16px; cursor: pointer;
  &:hover { background: #1a1f2e; }
`;
const HistoryDate = styled.span`font-size: 14px; font-weight: 700; color: #e2e8f0;`;
const HistoryMeta = styled.span`font-size: 12px; color: #64748b;`;
const HitsCount = styled.span`
  font-size: 13px; font-weight: 700; color: #00c6ff;
  background: rgba(0, 198, 255, 0.1); padding: 3px 10px; border-radius: 6px;
`;
const KisBadge = styled.span`
  font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 5px;
  background: rgba(245,158,11,0.12); color: #f59e0b; white-space: nowrap;
`;

const COLS = '1fr 90px 80px 80px 80px 80px';
const TH = styled.div`
  display: grid; grid-template-columns: ${COLS}; gap: 8px;
  padding: 10px 16px; background: #0d1117;
  font-size: 10px; color: #64748b; font-weight: 700; text-transform: uppercase;
  min-width: 520px;
`;
const TR = styled.div`
  display: grid; grid-template-columns: ${COLS}; gap: 8px;
  padding: 10px 16px; border-top: 1px solid #1e2030;
  font-size: 12px; min-width: 520px; transition: background 0.15s;
  &:hover { background: #1a1f2e; }
`;
const Badge = styled.span`
  font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 5px;
  background: rgba(0, 198, 255, 0.12); color: #00c6ff;
`;
const ChangeRate = styled.span`
  font-size: 12px; font-weight: 600;
  color: ${p => p.val > 0 ? '#10b981' : p.val < 0 ? '#ef4444' : '#64748b'};
`;

function VwapScreenerHistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [loading, setLoading] = useState({});

  useEffect(() => {
    apiService.getVwapScreenerHistory()
      .then(data => setHistory(data || []))
      .catch(() => {});
  }, []);

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (detail[id]) return;

    setLoading(prev => ({ ...prev, [id]: true }));
    try {
      const data = await apiService.getVwapScreenerResult(id);
      setDetail(prev => ({ ...prev, [id]: data }));
    } catch (e) {
      setDetail(prev => ({ ...prev, [id]: { hits: [] } }));
    } finally {
      setLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const fmt = n => n != null ? Number(n).toLocaleString() : '-';

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/vwap-screener')}><ArrowLeft size={18} /></BackBtn>
        <div>
          <PageTitle>VWAP 스크리너 이력</PageTitle>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>과거 스캔 결과 조회</div>
        </div>
      </TopBar>

      {history.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#475569', background: '#111827', borderRadius: 14 }}>
          스캔 이력이 없습니다. 스크리너를 먼저 실행해 주세요.
        </div>
      ) : (
        <HistoryList>
          {history.map(h => (
            <HistoryCard key={h.id}>
              <HistoryHeader onClick={() => toggleExpand(h.id)}>
                <HistoryDate>{h.date}</HistoryDate>
                <HistoryMeta>스캔: {fmt(h.totalScanned)}종목</HistoryMeta>
                <HitsCount>발굴 {h.hitsCount}종목</HitsCount>
                {h.kisStats?.headerFailed && (
                  <KisBadge title="KIS API 인증 실패 — 정적 시가총액 사용">⚠ KIS 인증 실패</KisBadge>
                )}
                {!h.kisStats?.headerFailed && h.kisStats?.failures > 0 && (
                  <KisBadge title={`${h.kisStats.attempts}건 중 ${h.kisStats.failures}건 조회 실패`}>
                    ⚠ KIS {h.kisStats.failures}/{h.kisStats.attempts} 실패
                  </KisBadge>
                )}
                <span style={{ marginLeft: 'auto', color: '#475569' }}>
                  {expanded === h.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </span>
              </HistoryHeader>

              {expanded === h.id && (
                loading[h.id] ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#475569', fontSize: 13 }}>
                    현재가 조회 중...
                  </div>
                ) : (detail[h.id]?.hits || []).length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#475569', fontSize: 13 }}>
                    발굴 종목 없음
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <TH>
                      <span>종목</span>
                      <span>현재가</span>
                      <span>등락</span>
                      <span>VWAP20</span>
                      <span>이격도</span>
                      <span>기울기</span>
                    </TH>
                    {(detail[h.id].hits || []).map(r => (
                      <TR key={r.symbol}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                          <div style={{ fontSize: 10, color: '#475569' }}>{r.symbol} · {r.market}</div>
                        </div>
                        <span style={{ fontWeight: 700 }}>{fmt(r.currentPrice ?? r.close)}</span>
                        <ChangeRate val={r.changeRate}>
                          {r.changeRate != null ? `${r.changeRate > 0 ? '+' : ''}${r.changeRate}%` : '-'}
                        </ChangeRate>
                        <span>{fmt(r.vwap20)}</span>
                        <Badge>{r.disparity}%</Badge>
                        <span style={{ color: '#10b981' }}>+{r.slope}%</span>
                      </TR>
                    ))}
                  </div>
                )
              )}
            </HistoryCard>
          ))}
        </HistoryList>
      )}
    </Page>
  );
}

export default VwapScreenerHistoryPage;
